import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import API from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { PortalPage, EmptyState, Pill } from "../../components/portal/PortalPage";
import { ProgressStepper } from "../../components/portal/ProgressStepper";
import { usePortal } from "../../components/portal/usePortal";
import { fmtDate, fmtMoney, requestStatusMap, titleCase } from "../../components/portal/portalUtils";
import { ClipboardText, Plus, Paperclip, MagnifyingGlass, Kanban } from "@phosphor-icons/react";
import { CardListSkeleton } from "../../components/Skeletons";
import { ForClientChip, FamilyFilter, useFamilyFilter } from "../../components/portal/FamilyFilter";

export default function ClientRequestsPage() {
  const { perms } = usePortal();
  const [view, setView] = useState("active");
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    setItems(null);
    API.get(`/client-hub/requests?view=${view}`).then(({ data }) => setItems(data.requests || [])).catch(() => setItems([]));
  }, [view]);

  const fam = useFamilyFilter();
  const filtered = (items || []).filter((r) => fam.matches(r) && (!q || `${r.reference} ${r.title} ${r.address} ${r.property_name} ${r.for_client || ""}`.toLowerCase().includes(q.toLowerCase())));

  return (
    <PortalPage
      title="Job Requests"
      subtitle="Every request you've raised, tracked step by step from submission to completion."
      testid="client-requests"
      actions={perms.can_request && <Button size="sm" onClick={() => navigate("/portal/requests/new")} data-testid="new-request-btn"><Plus size={14} className="mr-1" /> New Request</Button>}
    >
      <div className="flex flex-col sm:flex-row gap-2 sm:items-center justify-between mb-4">
        <div className="inline-flex rounded-md border border-border p-0.5 bg-muted/40">
          {[["active", "Active"], ["closed", "Closed"], ["all", "All"]].map(([k, l]) => (
            <button key={k} onClick={() => setView(k)} className={`px-3 py-1 text-xs rounded ${view === k ? "bg-card shadow font-semibold" : "text-muted-foreground"}`} data-testid={`requests-view-${k}`}>{l}</button>
          ))}
        </div>
        <div className="flex gap-2 items-center">
          <FamilyFilter fam={fam} testid="requests-client-filter" />
          <div className="relative sm:w-72">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-2.5 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search reference, title, property…" className="pl-8 h-8 text-xs" data-testid="requests-search" />
          </div>
        </div>
      </div>

      {items === null ? <CardListSkeleton rows={4} showHeader={false} /> : filtered.length === 0 ? (
        <EmptyState icon={ClipboardText} title={view === "active" ? "No active requests" : "No requests found"} text="Raise a request and we'll acknowledge it straight away."
          action={perms.can_request && <Button size="sm" onClick={() => navigate("/portal/requests/new")}>New Request</Button>} />
      ) : (
        <div className="space-y-3" data-testid="requests-list">
          {filtered.map((r) => (
            <Link key={r.id} to={`/portal/requests/${r.id}`} className="block border border-border rounded-lg bg-card p-4 hover:border-primary/40 card-lift" data-testid={`request-card-${r.id}`}>
              <div className="flex flex-col md:flex-row md:items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-[10px] font-mono text-muted-foreground">{r.reference}</span>
                    <Pill map={requestStatusMap(r)} value={r.status} />
                    <ForClientChip name={r.for_client} clientId={r.client_id} />
                    {r.request_type === "project" && <span className="text-[9px] px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300 font-semibold inline-flex items-center gap-0.5" data-testid={`request-project-${r.id}`}><Kanban size={10} /> PROJECT</span>}
                    {r.mandate && <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400 font-semibold" data-testid={`request-mandate-${r.id}`}>MANDATE {fmtMoney(r.mandate_limit)}</span>}
                    {r.priority === "urgent" && <span className="text-[9px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-semibold">URGENT</span>}
                  </div>
                  <p className="text-sm font-semibold truncate">{r.title}</p>
                  <p className="text-[11px] text-muted-foreground truncate">{r.property_name ? `${r.property_name} · ` : ""}{r.address || "—"}</p>
                  <p className="text-[10px] text-muted-foreground mt-1 flex items-center gap-2">
                    <span>Raised {fmtDate(r.created_at)}{r.created_by_name ? ` by ${r.created_by_name.replace(" (Client)", "")}` : ""}</span>
                    {r.attachment_count > 0 && <span className="flex items-center gap-0.5"><Paperclip size={10} /> {r.attachment_count}</span>}
                    <span>· {titleCase(r.category)}</span>
                  </p>
                </div>
                <div className="md:w-[46%] shrink-0"><ProgressStepper progress={r.progress} compact /></div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </PortalPage>
  );
}
